import { z } from 'zod';
import { libraryKeyFor } from './profile/keys.js';
import { QUESTION_VAULT_KINDS } from './profile/types.js';
/**
 * The credential vault's item kinds (spec 1.3, Phase 4): casting-site logins and the identity items (SSN, bank
 * accounts, EIN, work permit numbers) a person saves once and shares per agency. PURE and shared by server and client:
 * each kind's payload schema, its label and its mask (the only thing a list ever shows), the ABA routing checksum,
 * and the decode of the legacy form (a bare string copied verbatim from clients.profile_secure). The grant key of an
 * item is `libraryKeyFor(kind, purpose)` (profile/keys.ts, mirrored by SQL vault_library_key). New kinds need no
 * migration (user_credentials.kind is a slug).
 */
export const VAULT_KINDS = ['login', 'ssn', 'bank', 'ein', 'permit'];
/** The identity kinds (every vault kind but a login): the library's vault-stored questions name the same ones. */
export const IDENTITY_KINDS = QUESTION_VAULT_KINDS;
export const isIdentityKind = (k) => typeof k === 'string' && IDENTITY_KINDS.includes(k);
/** A bank item's purposes: each its own grant key, so unsharing direct deposit never ends the Coogan grant. */
export const BANK_PURPOSES = ['direct_deposit', 'coogan'];
/** The ABA routing number checksum: 3(d1 + d4 + d7) + 7(d2 + d5 + d8) + (d3 + d6 + d9) is a multiple of 10. */
export function abaChecksumOk(routing) {
    if (!/^\d{9}$/.test(routing))
        return false;
    const d = [...routing].map(Number);
    const sum = 3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + (d[2] + d[5] + d[8]);
    return sum % 10 === 0;
}
/** The routing number that is 9 digits but not a routing number (the checksum): its own plain message. */
const ROUTING_NOT_VALID = 'That routing number is not valid. Check the 9 digits.';
/** A bank item names at least one thing to recognise it by. */
const BANK_EMPTY = 'Enter the bank name, the account number or its last 4 digits.';
export const ssnPayload = z.object({ ssn: z.string().regex(/^\d{9}$/) });
export const bankPayload = z
    .object({
    bank_name: z.string().max(120),
    holder: z.string().max(120).optional(),
    routing: z.string().regex(/^\d{9}$/).refine(abaChecksumOk, ROUTING_NOT_VALID).optional(),
    account: z.string().regex(/^\d{4,17}$/).optional(),
    last4: z.string().regex(/^\d{4}$/).optional(),
    type: z.enum(['checking', 'savings']).optional(),
    purpose: z.enum(BANK_PURPOSES),
})
    .refine((b) => !!(b.bank_name.trim() || b.account || b.last4), { message: BANK_EMPTY });
export const einPayload = z.object({ ein: z.string().regex(/^\d{9}$/), entity_name: z.string().max(160).optional() });
/** A permit number as the child-performer panel stores it (up to 120 characters there, so the vault takes the same). */
export const permitPayload = z.object({ number: z.string().min(1).max(120), state: z.string().max(12).optional() });
const tail = (s, n) => s.slice(-n);
/** The last four digits of a bank item: its own `last4`, else its account number's. */
export const bankLast4 = (p) => p.last4 ?? (p.account ? tail(p.account, 4) : null);
export const VAULT_KIND_DEFS = {
    login: { kind: 'login', label: 'Login', payload: z.string(), mask: () => '••••', agencyListLabel: 'On file' },
    ssn: { kind: 'ssn', label: 'Social Security number', payload: ssnPayload, mask: (p) => `***-**-${tail(p.ssn, 4)}`, agencyListLabel: 'On file' },
    bank: {
        kind: 'bank',
        label: 'Bank account',
        payload: bankPayload,
        mask: (p) => {
            const b = p;
            const last4 = bankLast4(b);
            const name = b.bank_name.trim() || 'Bank';
            return last4 ? `${name} ****${last4}` : name;
        },
        agencyListLabel: 'On file',
        purposes: BANK_PURPOSES,
    },
    ein: { kind: 'ein', label: 'Business EIN', payload: einPayload, mask: (p) => `**-***${tail(p.ein, 4)}`, agencyListLabel: 'On file' },
    permit: { kind: 'permit', label: 'Work permit number', payload: permitPayload, mask: (p) => `Permit ****${tail(p.number.trim(), 2)}`, agencyListLabel: 'On file' },
};
const PURPOSE_LABELS = { direct_deposit: 'Direct deposit account', coogan: 'Coogan account' };
/** An identity item's label: the kind's, or for a bank item its purpose's. */
export function identityLabel(kind, purpose) {
    if (kind === 'bank' && purpose && Object.hasOwn(PURPOSE_LABELS, purpose))
        return PURPOSE_LABELS[purpose];
    return VAULT_KIND_DEFS[kind].label;
}
/**
 * THE slots a talent adds (the vault's "+ Add" choices, and what the server takes): an SSN and a direct deposit account
 * for anyone; a business EIN for an adult; a Coogan (trust) account and a work permit number for a child.
 */
export const IDENTITY_SLOTS = [
    { kind: 'ssn', purpose: null, for: ['self', 'dependent'] },
    { kind: 'bank', purpose: 'direct_deposit', for: ['self', 'dependent'] },
    { kind: 'bank', purpose: 'coogan', for: ['dependent'] },
    { kind: 'ein', purpose: null, for: ['self'] },
    { kind: 'permit', purpose: null, for: ['dependent'] },
].map((s) => ({ ...s, label: identityLabel(s.kind, s.purpose) }));
/** The slots offered to one audience, in their order. */
export const identitySlotsFor = (audience) => IDENTITY_SLOTS.filter((s) => s.for.includes(audience));
/** Whether a slot (kind and purpose) is one a talent adds for this audience. */
export const slotOffered = (kind, purpose, audience) => identitySlotsFor(audience).some((s) => s.kind === kind && (s.purpose ?? null) === (purpose ?? null));
/** The purpose an item of this kind is stored under (bank: its payload's; every other kind: none). */
export const purposeOf = (kind, payload) => kind === 'bank' ? (payload?.purpose ?? null) : null;
/** The grant key of an identity item (spec 1.3; profile/keys.ts libraryKeyFor, SQL vault_library_key). */
export const identityKeyOf = (kind, purpose) => libraryKeyFor(kind, purpose ?? null);
/** Normalize a US SSN to 9 digits; null for anything else (so a typo'd secret is never stored). Moved from routes/portal/secure.ts. */
export function normalizeSsn(raw) {
    const digits = String(raw ?? '').replace(/[^0-9]/g, '');
    return digits.length === 9 ? digits : null;
}
const digitsOf = (raw) => (raw === undefined || raw === null || raw === '' ? undefined : String(raw).replace(/[^0-9]/g, ''));
const textOf = (raw) => (raw === undefined || raw === null ? undefined : String(raw).trim());
const optionalText = (raw) => {
    const t = textOf(raw);
    return t ? t : undefined;
};
/** A payload as typed (dashes, spaces), made canonical before its schema checks it: digits only where digits are asked. */
function normalized(kind, raw) {
    switch (kind) {
        case 'ssn':
            return { ssn: digitsOf(raw.ssn) ?? '' };
        case 'ein':
            return { ein: digitsOf(raw.ein) ?? '', entity_name: optionalText(raw.entity_name) };
        case 'permit':
            return { number: textOf(raw.number) ?? '', state: optionalText(raw.state) };
        case 'bank':
            return {
                bank_name: textOf(raw.bank_name) ?? '',
                holder: optionalText(raw.holder),
                routing: digitsOf(raw.routing),
                account: digitsOf(raw.account),
                last4: digitsOf(raw.last4),
                type: optionalText(raw.type),
                purpose: raw.purpose,
            };
    }
}
const drop = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));
/** What to tell the person when a field is refused: one plain sentence per field, never a schema's own wording. */
const FIELD_MESSAGES = {
    ssn: { ssn: 'Enter the 9 digits of the Social Security number.' },
    ein: { ein: 'Enter the 9 digits of the EIN.', entity_name: 'Keep the business name to 160 characters.' },
    permit: { number: 'Enter the permit number, up to 120 characters.', state: 'Keep the state short, like CA.' },
    bank: {
        bank_name: 'Keep the bank name to 120 characters.',
        holder: 'Keep the account holder to 120 characters.',
        routing: 'Enter the 9 digits of the routing number.',
        account: 'Enter the account number: 4 to 17 digits.',
        last4: 'Enter the last 4 digits of the account.',
        type: 'Choose checking or savings.',
        purpose: 'Choose what the account is for.',
    },
};
/** PURE: the plain message of one refused field (a custom rule speaks for itself). */
function refusal(kind, issue) {
    if (!issue)
        return 'Check the details and try again.';
    if (issue.code === 'custom')
        return issue.message;
    return FIELD_MESSAGES[kind][String(issue.path[0] ?? '')] ?? 'Check the details and try again.';
}
/** Check a payload for a kind (as typed or as stored): its canonical form, or why it is refused (a plain sentence). */
export function parseIdentityPayload(kind, raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return { ok: false, error: 'Fill in the details first.' };
    const r = VAULT_KIND_DEFS[kind].payload.safeParse(drop(normalized(kind, raw)));
    if (!r.success)
        return { ok: false, error: refusal(kind, r.error.issues[0]) };
    return { ok: true, value: r.data };
}
/**
 * A decrypted item's payload: the payload object, or the legacy form (a bare string copied verbatim from
 * profile_secure or a performer column: an SSN or EIN as typed, a permit number, a Coogan account's last four digits
 * with `purpose`). Null when it cannot be read as this kind (unparseable: the backfill keeps such a value verbatim).
 */
export function decodeIdentityPayload(kind, decrypted, purpose) {
    if (typeof decrypted === 'string' || typeof decrypted === 'number') {
        const s = String(decrypted).trim();
        if (!s)
            return null;
        switch (kind) {
            case 'ssn':
                return normalizeSsn(s) ? { ssn: normalizeSsn(s) } : null;
            case 'ein': {
                const r = parseIdentityPayload('ein', { ein: s });
                return r.ok ? r.value : null;
            }
            case 'permit': {
                const r = parseIdentityPayload('permit', { number: s });
                return r.ok ? r.value : null;
            }
            case 'bank': {
                const r = parseIdentityPayload('bank', { bank_name: '', last4: s, purpose });
                return r.ok ? r.value : null;
            }
        }
    }
    const r = parseIdentityPayload(kind, decrypted);
    return r.ok ? r.value : null;
}
/** An item's mask (what lists show). */
export const identityMask = (kind, payload) => VAULT_KIND_DEFS[kind].mask(payload);
/** A revealed item as text (an agency's reveal, the owner's): an SSN or EIN formatted, a bank item's details on one line. */
export function identityDisplay(kind, payload) {
    switch (kind) {
        case 'ssn': {
            const s = payload.ssn;
            return `${s.slice(0, 3)}-${s.slice(3, 5)}-${s.slice(5)}`;
        }
        case 'ein': {
            const p = payload;
            return `${p.ein.slice(0, 2)}-${p.ein.slice(2)}${p.entity_name ? ` (${p.entity_name})` : ''}`;
        }
        case 'permit': {
            const p = payload;
            return p.state ? `${p.number} (${p.state})` : p.number;
        }
        case 'bank': {
            const p = payload;
            const parts = [p.bank_name.trim() || 'Bank'];
            if (p.holder)
                parts.push(`holder ${p.holder}`);
            if (p.routing)
                parts.push(`routing ${p.routing}`);
            if (p.account)
                parts.push(`account ${p.account}`);
            else if (p.last4)
                parts.push(`account ending ${p.last4}`);
            if (p.type)
                parts.push(p.type);
            return parts.join(', ');
        }
    }
}
/** The last four digits of an item (the Accept card shows only these, after a reveal). */
export function identityLast4(kind, payload) {
    switch (kind) {
        case 'ssn':
            return tail(payload.ssn, 4);
        case 'ein':
            return tail(payload.ein, 4);
        case 'permit':
            return tail(payload.number.trim(), 4);
        case 'bank':
            return bankLast4(payload);
    }
}
/** Two payloads of one kind are the same value (decrypted, canonical): what "the talent item differs" means. */
export function identitiesEqual(kind, a, b) {
    switch (kind) {
        case 'ssn':
            return a.ssn === b.ssn;
        case 'ein':
            return a.ein === b.ein;
        case 'permit':
            return a.number.trim().toLowerCase() === b.number.trim().toLowerCase();
        case 'bank': {
            const x = a;
            const y = b;
            return (x.purpose === y.purpose &&
                x.bank_name.trim().toLowerCase() === y.bank_name.trim().toLowerCase() &&
                (x.routing ?? null) === (y.routing ?? null) &&
                (x.account ?? null) === (y.account ?? null) &&
                bankLast4(x) === bankLast4(y));
        }
    }
}
/** Create an identity item (the owner or a guardian of the record). `share_with`: client rows of the record to share it with at once. */
export const identityCreateSchema = z.object({
    record_id: z.string().uuid(),
    kind: z.enum(IDENTITY_KINDS),
    payload: z.record(z.string(), z.unknown()),
    share_with: z.array(z.string().uuid()).max(20).optional(),
});
export const identityPatchSchema = z.object({ payload: z.record(z.string(), z.unknown()) });
export const identityShareSchema = z.object({ client_id: z.string().uuid(), on: z.boolean() });
//# sourceMappingURL=vault-kinds.js.map